import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import {
  mapStyles,
  mdMapConfig,
  paMapConfig,
  vaMapConfig,
  mapStyle,
  mapboxToken,
  scrollZoomGuard,
} from "../../config";
import {
  filterSchools,
  filterS4sGrantees,
  filterMeaGrantees,
  buildFilterOptions,
  buildS4sFilterOptions,
  buildMeaGranteesFilterOptions,
  normalize,
  getBottomCenterOffset,
  getStateBounds,
  getStateFeature,
  getStateMask,
  worldMask,
} from "../../utils";
import Button from "../Button";
import SchoolPopup from "../SchoolPopup";
import states from "../../data/states";
import * as layers from "../../layers";
import { views, stateCodes } from "../../constants";
import {
  AppFilters,
  MapFiltersButton,
  Legend,
  StateMapButtons,
  StateMapViewToggle,
} from "./styled.js";
import { Wrapper, Container, MapSearch } from "../App/styled.js";
import { MapFadeIn } from "../MapChrome/styled.js";
import { LegendSwatch } from "../Legend/styled.js";
import DistrictPopup from "../DistrictPopup";
import {
  PIN_DEFAULT,
  fiscalYearColor,
  fiscalYearMarker,
  registerPinImages,
} from "../../pins";

// The style's city labels get moved to the top of the stack below (so they can
// bleed past the state outline), which would otherwise bury the pins. These go
// back above them afterwards, listed in the order they're declared in the JSX
// so each move to the top preserves their relative stacking.
const MARKER_LAYER_IDS = [
  layers.schoolsMarkerLayer.id,
  layers.caseStudiesMarkerLayer.id,
  layers.schoolsCircleLayer.id,
  layers.schoolsCircleHoverLayer.id,
  layers.schoolsClusterLayer.id,
  layers.clustersHoverLayer1.id,
  layers.clustersHoverLayer2.id,
  layers.clustersHoverLayer3.id,
  layers.clustersHoverLayer4.id,
  layers.schoolsClusterCountLayer.id,
];


function StateMap({
  className,
  stateCode,
  stateName,
  schoolsData,
  onViewChange,
  mapView,
  enableS4sGrantees,
  enableMeaGrantees,
  ...props
}) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  let config;
  switch (stateCode) {
    case stateCodes.MD:
      config = mdMapConfig;
      break;
    case stateCodes.PA:
      config = paMapConfig;
      break;
    case stateCodes.VA:
      config = vaMapConfig;
      break;
    default:
      break;
  }
  const mapConfig = useMemo(
    () => (isMobile ? { ...config, zoom: config.mobileZoom } : config),
    [isMobile, config]
  );

  // World-minus-this-state polygon for the "mask everything outside the
  // state" fill layer, so it has no gaps over ocean, the Great Lakes, or
  // neighboring countries.
  const stateMask = useMemo(() => getStateMask(stateName), [stateName]);
  // The state's own (non-inverted) shape, used to let city labels for
  // cities actually inside the state bleed over the exterior mask instead
  // of being clipped by it.
  const stateFeature = useMemo(() => getStateFeature(stateName), [stateName]);

  const isGranteeView =
    mapView === views.MEA_GRANTEES || mapView === views.S4S_GRANTEES;
  const schoolMarker = isGranteeView ? fiscalYearMarker : PIN_DEFAULT;
  const [caseStudy, setCaseStudy] = useState("");
  const [activeDistrict, setActiveDistrict] = useState("");
  const [activeHoverDistrict, setActiveHoverDistrict] = useState("");
  const [school, setSchool] = useState(null);
  const [schools, setSchools] = useState(schoolsData);
  const [district, setDistrict] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [stateDistricts, setStateDistricts] = useState(null);
  const [mapViewState, setMapViewState] = useState(mapConfig);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    systemSize: [],
    solarOwnership: [],
    structureType: [],
    caseStudies: [],
    batteryStorage: [],
    cleanEnergyTechnology: [],
    yearInstalled: [],
  });
  const [s4sActiveFilters, setS4sActiveFilters] = useState({
    grantFiscalYear: [],
    solarCapacity: [],
    grantAmount: [],
    solarOwnership: [],
  });
  const [meaActiveFilters, setMeaActiveFilters] = useState({
    cleanEnergyTechnology: [],
    fiscalYear: [],
    grantAmount: [],
    aoiCategory: [],
  });
  const [legendOpen, setLegendOpen] = useState(false);

  const filterOptions =
    mapView === views.S4S_GRANTEES
      ? buildS4sFilterOptions(schoolsData)
      : mapView === views.MEA_GRANTEES
      ? buildMeaGranteesFilterOptions(schoolsData)
      : buildFilterOptions(schoolsData);

  // Whichever facet this view's fiscal years live under. The legend reads
  // from the data too, so a new year's cohort shows up on its own.
  const fiscalYears =
    (mapView === views.S4S_GRANTEES
      ? filterOptions.grantFiscalYear
      : filterOptions.fiscalYear) || [];

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeSchoolsCircle, setActiveSchoolsCircle] = useState("");
  const [activeClusterLayer1, setActiveClusterLayer1] = useState("");
  const [activeClusterLayer2, setActiveClusterLayer2] = useState("");
  const [activeClusterLayer3, setActiveClusterLayer3] = useState("");
  const [activeClusterLayer4, setActiveClusterLayer4] = useState("");
  const [interacted, setInteracted] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef();
  const resizeObserverRef = useRef();
  const styleLoadedRef = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.resize();
      const bounds = getStateBounds(stateName);
      if (bounds) {
        mapRef.current?.fitBounds(bounds, {
          padding: mapConfig.padding,
          duration: 0,
        });
      } else {
        setMapViewState(mapConfig);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserverRef.current?.disconnect();
    };
  }, [stateName, mapConfig]);

  useEffect(() => {
    setLegendOpen(false);
  }, [mapView]);

  // mapView swaps which <Map> instance is mounted (schools/s4s/mea vs.
  // districts), each firing its own onStyleData — reset so the new one
  // starts hidden until its own fitBounds has run. The timer is a safety
  // net in case 'styledata' never fires (e.g. a stalled style request), so
  // the map doesn't stay hidden indefinitely.
  useEffect(() => {
    setMapReady(false);
    styleLoadedRef.current = false;
    const timer = setTimeout(() => setMapReady(true), 4000);
    return () => clearTimeout(timer);
  }, [mapView]);

  const revealMap = useCallback(() => {
    registerPinImages(mapRef.current.getMap());
    mapRef.current?.resize();
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(() => mapRef.current?.resize());
    resizeObserverRef.current.observe(mapRef.current.getContainer());
    const bounds = getStateBounds(stateName);
    if (bounds) {
      mapRef.current.fitBounds(bounds, {
        padding: mapConfig.padding,
        duration: 0,
      });
    }

    // City labels are hidden by the exterior mask by default (same as
    // roads/water), since the mask sits above everything including labels.
    // For cities actually inside the state, move the style's city label
    // layers above our own mask layer (and filter them to only the cities
    // within this state) so their text can bleed past the state outline at
    // the edges instead of getting clipped by it. Moving + filtering the
    // existing layers (rather than adding filtered clones on top) sidesteps
    // z-order races with react-map-gl's own layer insertion/reordering.
    if (mapView !== views.STATE_DISTRICTS && stateFeature) {
      const map = mapRef.current.getMap();
      const style = map.getStyle();
      ["Cities SM", "Cities MD", "Cities LG"].forEach((id) => {
        const original = style.layers.find((l) => l.id === id);
        if (!original) return;
        const withinFilter = ["within", stateFeature];
        map.setFilter(
          id,
          original.filter ? ["all", original.filter, withinFilter] : withinFilter
        );
        map.moveLayer(id);
      });
      // Cities are now above everything, pins included, so put the markers
      // back on top. Any of these that react-map-gl hasn't added yet lands
      // on top on its own when it does.
      MARKER_LAYER_IDS.forEach((id) => {
        if (map.getLayer(id)) map.moveLayer(id);
      });
      // These changes don't always trigger an immediate repaint on their
      // own, which can leave the labels invisible for a beat after the
      // view first loads.
      map.triggerRepaint();
    }
    setMapReady(true);
  }, [stateName, mapConfig.padding, mapView, stateFeature]);

  // Reveal as soon as the style (layers/camera) is ready, rather than
  // waiting for 'load', which also waits on the viewport's tiles to finish
  // downloading. Tiles then populate progressively underneath, same as
  // before the fade-in was added.
  const onStyleData = useCallback(
    (event) => {
      if (event.dataType !== "style" || styleLoadedRef.current) return;
      styleLoadedRef.current = true;
      revealMap();
    },
    [revealMap]
  );

  useEffect(() => {
    fetch(`/${stateCode.toLowerCase()}-data.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json) => {
        setDistricts(json);
        setStateDistricts(json);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [stateCode]);

  const handleReset = () => {
    const bounds = getStateBounds(stateName);
    if (bounds) {
      mapRef.current.fitBounds(bounds, {
        padding: mapConfig.padding,
        duration: 1000,
      });
    } else {
      setMapViewState(mapConfig);
    }
    setSchool(null);
    setDistrict(null);
    setCaseStudy(null);
    setActiveDistrict("");
  };

  const onMove = useCallback(
    ({ viewState }) => {
      if (viewState.zoom < mapConfig.minZoom) {
        viewState.zoom = mapConfig.minZoom;
      }
      setMapViewState(viewState);
    },
    [mapConfig.minZoom]
  );

  const getPlaceholder = () => {
    if (isMobile) return "Search here";

    return mapView === views.STATE_SCHOOLS ||
      mapView === views.S4S_GRANTEES ||
      mapView === views.MEA_GRANTEES
      ? "Search by school name, district, or city"
      : "School District Name";
  };

  // const paSchools = schools.filter((x) => {
  //   return x.properties.state === "PA";
  // });

  const onClick = useCallback((event) => {
    setInteracted(true);
    event.originalEvent.stopPropagation();
    const feature = event.features && event.features[0];
    if (!feature) return;

    switch (feature.layer.id) {
      case "schools-circle":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-cluster":
        mapRef.current.flyTo({
          center: [event.lngLat.lng, event.lngLat.lat],
          duration: 1000,
          zoom: 8,
        });
        break;
      case "schools-marker":
        if (!feature.properties.cluster) {
          setSchool(feature.properties);
          setCaseStudy(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: Math.max(mapRef.current.getZoom(), 8),
            offset: getBottomCenterOffset(mapRef.current),
          });
        }
        break;
      case "case-studies-marker":
        if (!feature.properties.cluster) {
          setCaseStudy(feature.properties);
          setSchool(null);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            zoom: Math.max(mapRef.current.getZoom(), 8),
            offset: getBottomCenterOffset(mapRef.current),
          });
        }
        break;
      case "state-district-hover":
        if (feature.properties.longitude && feature.properties.latitude) {
          setDistrict(feature.properties);
          setActiveDistrict(feature.properties.school_dis);
          mapRef.current.flyTo({
            center: [feature.properties.longitude, feature.properties.latitude],
            duration: 1000,
            offset: [0, 100],
          });
        }
        break;
      default:
        break;
    }
  }, []);

  const onHover = useCallback((event) => {
    const feature = event.features && event.features[0];
    if (
      feature?.layer.id === "state-district-fills-capacity" ||
      feature?.layer.id === "state-district-hover"
    ) {
      setActiveHoverDistrict(feature.properties.school_dis);
      mapRef.current.getCanvas().style.cursor = "pointer";
    } else {
      mapRef.current.getCanvas().style.cursor = "";
    }
    if (!feature) {
      setActiveHoverDistrict("");
    }
  }, []);

  const onMouseEnter = useCallback((event) => {
    const feature = event.features && event.features[0];
    switch (feature.layer.id) {
      case "schools-marker":
        break;
      case "case-studies-marker":
        break;
      case "schools-circle":
        setActiveSchoolsCircle(feature.properties.id);
        break;
      default:
        break;
    }
    if (feature?.layer.id === "schools-cluster") {
      const pointCount = feature.properties.point_count;
      if (pointCount <= 50) {
        setActiveClusterLayer1(feature.properties.cluster_id);
      } else if (pointCount <= 99) {
        setActiveClusterLayer2(feature.properties.cluster_id);
      } else if (pointCount <= 249) {
        setActiveClusterLayer3(feature.properties.cluster_id);
      } else {
        setActiveClusterLayer4(feature.properties.cluster_id);
      }
    }
  }, []);

  const onMouseLeave = useCallback((event) => {
    setActiveHoverDistrict("");
    setActiveClusterLayer1("");
    setActiveClusterLayer2("");
    setActiveClusterLayer3("");
    setActiveClusterLayer4("");
  }, []);

  const handleToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
  };

  const handleS4sFilter = (filters) => {
    setS4sActiveFilters(filters);
  };

  const handleMeaFilter = (filters) => {
    setMeaActiveFilters(filters);
  };

  const handleSearch = (searchTerm) => {
    setActiveSearchTerm(searchTerm);
  };

  const handleDistrictClose = () => {
    setDistrict(null);
    setActiveDistrict("");
  };

  useEffect(() => {
    if (mapView === views.S4S_GRANTEES) {
      setSchools(filterS4sGrantees(activeSearchTerm, s4sActiveFilters, schoolsData));
    } else if (mapView === views.MEA_GRANTEES) {
      setSchools(filterMeaGrantees(activeSearchTerm, meaActiveFilters, schoolsData));
    } else {
      setSchools(filterSchools(activeSearchTerm, activeFilters, schoolsData));
    }
  }, [
    activeSearchTerm,
    activeFilters,
    s4sActiveFilters,
    meaActiveFilters,
    schoolsData,
    mapView,
  ]);

  // Switching between the School/S4S/MEA views swaps the entire dataset
  // (each with its own id space), so any open popup from the previous view
  // is no longer meaningful — always close on view change.
  useEffect(() => {
    setSchool(null);
    setCaseStudy(null);
  }, [mapView]);

  // Within the same view, only close a popup once its marker actually drops
  // out of the filtered/searched result set, rather than on every change.
  useEffect(() => {
    if (school && !schools.some((x) => x.properties.id === school.id)) {
      setSchool(null);
    }
  }, [schools, school]);

  useEffect(() => {
    if (caseStudy && !schools.some((x) => x.properties.id === caseStudy.id)) {
      setCaseStudy(null);
    }
  }, [schools, caseStudy]);

  const handleDistrictSearch = (searchTerm) => {
    if (searchTerm === "") {
      setDistricts(stateDistricts);
      return;
    }
    const filtered = stateDistricts.features.filter(function (x) {
      var district = normalize(x.properties.school_dis);
      return district.indexOf(searchTerm) > -1;
    });
    setDistricts({
      type: "FeatureCollection",
      features: filtered,
    });
  };

  const handleMapView = (view) => {
    onViewChange(view);
  };

  return (
    <>
      {(mapView === views.STATE_SCHOOLS ||
        mapView === views.S4S_GRANTEES ||
        mapView === views.MEA_GRANTEES) && (
        <MapFiltersButton
          activeFilters={
            mapView === views.S4S_GRANTEES
              ? s4sActiveFilters
              : mapView === views.MEA_GRANTEES
              ? meaActiveFilters
              : activeFilters
          }
          results={schools}
          onClick={handleToggle}
        />
      )}
      <StateMapViewToggle>
        <Button
          type="blue"
          isActive={mapView === views.STATE_SCHOOLS}
          onClick={() => handleMapView(views.STATE_SCHOOLS)}
        >
          School
        </Button>
        <Button
          type="blue"
          isActive={mapView === views.STATE_DISTRICTS}
          onClick={() => handleMapView(views.STATE_DISTRICTS)}
        >
          School District
        </Button>
        {enableS4sGrantees && (
          <Button
            type="blue"
            isActive={mapView === views.S4S_GRANTEES}
            onClick={() => handleMapView(views.S4S_GRANTEES)}
          >
            Solar For Schools Grant Program
          </Button>
        )}
        {enableMeaGrantees && (
          <Button
            type="blue"
            isActive={mapView === views.MEA_GRANTEES}
            onClick={() => handleMapView(views.MEA_GRANTEES)}
          >
            MEA Grant Program
          </Button>
        )}
      </StateMapViewToggle>
      <Wrapper>
        <MapSearch
          placeholder={getPlaceholder()}
          onSearch={(searchTerm) => {
            mapView === views.STATE_SCHOOLS ||
            mapView === views.S4S_GRANTEES ||
            mapView === views.MEA_GRANTEES
              ? handleSearch(searchTerm)
              : handleDistrictSearch(searchTerm);
          }}
        />
        <Container>
          {mapView === views.STATE_SCHOOLS && (
            <AppFilters
              filterOptions={filterOptions}
              activeFilters={activeFilters}
              results={schools}
              isOpen={filtersOpen}
              onToggle={handleToggle}
              onFilter={handleFilter}
            />
          )}
          {mapView === views.S4S_GRANTEES && (
            <AppFilters
              type="s4sGrantees"
              filterOptions={filterOptions}
              activeFilters={s4sActiveFilters}
              results={schools}
              isOpen={filtersOpen}
              onToggle={handleToggle}
              onFilter={handleS4sFilter}
            />
          )}
          {mapView === views.MEA_GRANTEES && (
            <AppFilters
              type="meaGrantees"
              filterOptions={filterOptions}
              activeFilters={meaActiveFilters}
              results={schools}
              isOpen={filtersOpen}
              onToggle={handleToggle}
              onFilter={handleMeaFilter}
            />
          )}
          {isGranteeView && fiscalYears.length > 0 && (
            <Legend
              title="Grant Fiscal Year"
              isOpen={legendOpen}
              onClose={() => setLegendOpen(false)}
            >
              {fiscalYears.map((year) => (
                <li key={year}>
                  <LegendSwatch color={fiscalYearColor(year)}></LegendSwatch>
                  {year}
                </li>
              ))}
            </Legend>
          )}
          {mapView === views.STATE_DISTRICTS && (
            <Legend
              title="Solar Capacity"
              isOpen={legendOpen}
              onClose={() => setLegendOpen(false)}
            >
              <li>
                <LegendSwatch
                  color={mapStyles.legendTier6Color}
                ></LegendSwatch>
                1000+ kW
              </li>
              <li>
                <LegendSwatch
                  color={mapStyles.legendTier5Color}
                ></LegendSwatch>
                500-999 kW
              </li>
              <li>
                <LegendSwatch
                  color={mapStyles.legendTier4Color}
                ></LegendSwatch>
                250-499 kW
              </li>
              <li>
                <LegendSwatch
                  color={mapStyles.legendTier3Color}
                ></LegendSwatch>
                51-249 kW
              </li>
              <li>
                <LegendSwatch
                  color={mapStyles.legendTier2Color}
                ></LegendSwatch>
                1-50 kW
              </li>
              <li>
                <LegendSwatch
                  color="#FFFFFF"
                  border="1px solid #D1D1D1"
                ></LegendSwatch>
                0 kW
              </li>
            </Legend>
          )}
          {(mapView === views.STATE_SCHOOLS ||
            mapView === views.S4S_GRANTEES ||
            mapView === views.MEA_GRANTEES) && (
            <MapFadeIn $ready={mapReady}>
            <Map
              ref={mapRef}
              {...mapViewState}
              id="schoolsMap"
              onStyleData={onStyleData}
              onMove={onMove}
              scrollZoom={scrollZoomGuard === "click-to-activate" ? interacted : true}
              cooperativeGestures={scrollZoomGuard === "cooperative"}
              width="100%"
              height="100%"
              mapStyle={mapStyle}
              mapboxAccessToken={mapboxToken}
              onClick={onClick}
              onMouseMove={onHover}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              interactiveLayerIds={[
                "schools-marker",
                "case-studies-marker",
                "schools-circle",
                "schools-cluster",
              ]}
            >
              <NavigationControl showCompass={false} />
              <Source id="states" type="geojson" data={states}>
                <Layer id="state-borders" {...layers.stateBordersStatesLayer} />
              </Source>
              <Source id="state-mask" type="geojson" data={stateMask}>
                <Layer
                  id="state-fills"
                  type="fill"
                  paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
                />
              </Source>
              <Source id="states" type="geojson" data={states}>
                <Layer
                  id="state-borders"
                  filter={["==", "name", stateName]}
                  {...layers.stateBorderLayer}
                />
              </Source>
              <Source
                id="schools-data"
                type="geojson"
                data={{ type: "FeatureCollection", features: schools }}
                cluster={true}
                clusterMaxZoom={6}
                clusterRadius={50}
              >
                <Layer
                  {...layers.schoolsMarkerLayer}
                  layout={{
                    ...layers.schoolsMarkerLayer.layout,
                    "icon-image": schoolMarker,
                  }}
                />
                <Layer {...layers.caseStudiesMarkerLayer} />
                <Layer {...layers.schoolsCircleLayer} />
                <Layer
                  {...layers.schoolsCircleHoverLayer}
                  filter={["==", "id", activeSchoolsCircle]}
                />
                <Layer {...layers.schoolsClusterLayer} />
                <Layer
                  {...layers.clustersHoverLayer1}
                  filter={["==", "cluster_id", activeClusterLayer1]}
                />
                <Layer
                  {...layers.clustersHoverLayer2}
                  filter={["==", "cluster_id", activeClusterLayer2]}
                />
                <Layer
                  {...layers.clustersHoverLayer3}
                  filter={["==", "cluster_id", activeClusterLayer3]}
                />
                <Layer
                  {...layers.clustersHoverLayer4}
                  filter={["==", "cluster_id", activeClusterLayer4]}
                />
                <Layer {...layers.schoolsClusterCountLayer} />
              </Source>
              {caseStudy && (
                <SchoolPopup
                  anchor="bottom"
                  longitude={Number(caseStudy.longitude)}
                  latitude={Number(caseStudy.latitude)}
                  onClose={() => setCaseStudy(null)}
                  closeOnClick={false}
                  data={caseStudy}
                  mapView={mapView}
                ></SchoolPopup>
              )}
              {school && (
                <SchoolPopup
                  anchor="bottom"
                  longitude={Number(school.longitude)}
                  latitude={Number(school.latitude)}
                  onClose={() => setSchool(null)}
                  closeOnClick={false}
                  data={school}
                  mapView={mapView}
                ></SchoolPopup>
              )}
            </Map>
            </MapFadeIn>
          )}
          {mapView === views.STATE_DISTRICTS && (
            <MapFadeIn $ready={mapReady}>
            <Map
              ref={mapRef}
              {...mapViewState}
              id="statesMap"
              onStyleData={onStyleData}
              onMove={onMove}
              scrollZoom={scrollZoomGuard === "click-to-activate" ? interacted : true}
              cooperativeGestures={scrollZoomGuard === "cooperative"}
              width="100%"
              height="100%"
              mapStyle={mapStyle}
              mapboxAccessToken={mapboxToken}
              onClick={onClick}
              onMouseMove={onHover}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              interactiveLayerIds={[
                "state-district-fills-capacity",
                "state-district-hover",
                "state-district-borders",
              ]}
            >
              <NavigationControl showCompass={false} />
              <Source id="states" type="geojson" data={states}>
                <Layer id="state-borders" {...layers.stateBordersStatesLayer} />
              </Source>
              <Source id="state-mask" type="geojson" data={worldMask}>
                <Layer
                  id="state-fills"
                  type="fill"
                  paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
                />
              </Source>
              <Source
                id="all-district-data"
                type="geojson"
                data={stateDistricts}
              >
                <Layer {...layers.stateDistrictFills} />
                <Layer {...layers.stateAllDistrictBorders} />
              </Source>
              <Source id="district-data" type="geojson" data={districts}>
                <Layer
                  {...layers.stateDistrictFillsCapacity}
                  layout={{
                    visibility:
                      mapView === views.STATE_DISTRICTS ? "visible" : "none",
                  }}
                />
                <Layer {...layers.stateDistrictBorders} />
                <Layer
                  id="state-district-hover"
                  type="fill"
                  paint={{ "fill-color": "#fff", "fill-opacity": 0.2 }}
                  filter={["==", "school_dis", activeHoverDistrict]}
                />
                <Layer
                  {...layers.stateActiveDistrictBorder}
                  filter={["==", "school_dis", activeDistrict]}
                />
              </Source>
              {district && (
                <DistrictPopup
                  anchor="bottom"
                  longitude={Number(district.longitude)}
                  latitude={Number(district.latitude)}
                  onClose={handleDistrictClose}
                  closeOnClick={false}
                  data={district}
                ></DistrictPopup>
              )}
            </Map>
            </MapFadeIn>
          )}
          <StateMapButtons
            mapRef={mapRef}
            onReset={handleReset}
            showReset={true}
            showLegend={
              mapView === views.S4S_GRANTEES ||
              mapView === views.MEA_GRANTEES ||
              mapView === views.STATE_DISTRICTS
            }
            legendOpen={legendOpen}
            onToggleLegend={() => setLegendOpen((open) => !open)}
          />
        </Container>
      </Wrapper>
    </>
  );
}

export default StateMap;
